import { useSelector } from "react-redux";
import { Grid, MenuItem } from "@material-ui/core";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from 'uuid';

import { isStrictEquals } from '../../utils/general/general-utils';
import QCXFormStepWizard from "../../shared-components/form-step-wizard/QCXFormStepWizard";
import QCXSelectDespesaReceitaAutocomplete from "../../shared-components/select-despesa-receita/QCXSelectDespesaReceitaAutocomplete";
import QCXSelectMoedaAutocomplete from "../../shared-components/final-select-moeda-field/QCXSelectMoedaAutocomplete";
import QCXFinalTextField from "../../shared-components/final-text-field/QCXFinalTextField";
import QCXFinalBondManagerOld from "../../shared-components/final-gerenciador-relacionamento/QCXFinalBondManagerOld";
import QCXFinalAlternativeCurrencyField from "../../shared-components/final-currency-field/QCXFinalAlternativeCurrencyField";
import QCXTextField from "../../shared-components/text-field/QCXTextField";
import { masterCustosSelectors, masterCustosSliceActions } from "../../features/master-custos/masterCustosSlice";
import { selectDespesaReceita, selectDespesasReceitas } from "../../features/despesa-receita/despesaReceitaSlice";
import { selectMoedas } from "../../features/moeda/moedaSlice";
import QCXFinalSelectGenericoAutocomplete from "../../shared-components/select-tipo-frete/QCXFinalSelectGenericoAutocomplete";

export default function QCXMasterCustoForm({
    initialValues,
    handleSubmit,
    isConsult,
    isUpdate,
    controlComponentProps,
    authInfo = {},
    tipoPagamento,
    setTipoPagamento,
    declarado,
    setDeclarado,
    setMasterCustos,
    requiredRoles = [],
}) {
    const { t } = useTranslation();

    const handleTipoPagamentoChange = (event) => {
        setTipoPagamento(event.target.value)
    };

    const handleDeclaradoChange = (event) => {
        setDeclarado(event.target.value)
    };

    const handleCustoChange = (custos) => {
        setMasterCustos(custos)
    };

    const configureDespesaReceitaFilterPredicateWith = useMemo(
        () => [
          {
            name: 'operacional',
            value: true,
          },
        ],
        []
      );



    const referenciaClienteListProps = useMemo(
        () => ({
            name: 'masterCustos',
            columns: [
                {
                    field: 'despesaReceita',
                    headerName: "Despesa / Receita",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.despesaReceita?.description || '-',
                },
                {
                    field: 'moeda',
                    headerName: "Moeda",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.moeda?.description || '-',
                },
                {
                    field: 'tipoPagamento',
                    headerName: "Tipo Pagamento",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.tipoPagamento || '-',
                },
                {
                    field: 'valorCompraMoeda',
                    headerName: "Valor comprar",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.valorCompraMoeda || '-',
                },
                {
                    field: 'valorCompraReais',
                    headerName: "Valor Venda",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.valorCompraReais || '-',
                },
                {
                    field: 'declarado',
                    headerName: "Declarado",
                    headerAlign: 'left',
                    align: 'left',
                    flex: 140,
                    valueGetter: ({ row }) => row?.declarado || '-',
                },
            ],
        }),
        []
    );

    const despesas = useSelector(selectDespesasReceitas);

    const moedas = useSelector(selectMoedas);

    const referenciaClienteFormProps = useMemo(
  () => ({
    rootName: 'ignorableFields.referenciaCliente',
    fields: [
      { name: 'despesaReceita.id', label: "Despesa / Receita" },
      { name: 'moeda.id', label: "Moeda" },
      { name: 'valorComprar', label: "Valor Comprar" },
     { name: 'tipoPagamento', label: "Tipo Pagamento" },
     { name: 'declarado', label: "Declarado" },
    ],
  }),
  []
);


    const referenciaClienteReducerConfig = useMemo(
        () => ({
            selectors: {
                selectStatus: masterCustosSelectors.selectStatus,
                selectMode: masterCustosSelectors.selectMode,
                selectModel: masterCustosSelectors.selectModel,
            },
            actions: {
                loading: masterCustosSliceActions.loading,
                resetStatus: masterCustosSliceActions.resetStatus,
                changeToUpdateMode: masterCustosSliceActions.changeToUpdateMode,
                changeToCreateMode: masterCustosSliceActions.changeToCreateMode,
                resetMode: masterCustosSliceActions.resetMode,
                setModel: masterCustosSliceActions.setModel,
                resetModel: masterCustosSliceActions.resetModel,
            },
        }), []);

    const handleAlreadyExists = useCallback(() => false, []);

    const handleAtualizarCustos = useCallback(
        async (currentVinculo, currentVinculos, handleSuccessUpdateVinculo) => {
            const updatedVinculo = {
                ...currentVinculo,
                referencia: currentVinculo?.referencia,
            };

            const updatedVinculos =
                currentVinculos?.map((existingVinculo) =>
                    isStrictEquals(existingVinculo?.id, updatedVinculo?.id) ? updatedVinculo : existingVinculo
                ) || currentVinculos;

            handleSuccessUpdateVinculo(updatedVinculos);
        }, []);

    const handleAdicionarMasterCusto = useCallback(async (currentVinculo, currentVinculos, handleSuccessAddVinculo) => {

        const selectMoeda = moedas.find(x => x?.id === currentVinculo?.moeda?.id);

        const selectDespesa = despesas.find(x => x?.id === currentVinculo?.despesaReceita?.id);

        const vinculo = {
            id: uuid(),
            despesaReceita: selectDespesa,
            moeda: selectMoeda,
            tipoPagamento: currentVinculo?.tipoPagamento,
            valorCompraMoeda: currentVinculo?.valorComprar,
            valorCompraReais: currentVinculo?.valorComprar,
            valorCompraDolar: currentVinculo?.valorComprar,
            declarado: currentVinculo?.declarado,
        };

        const updatedVinculos = [...currentVinculos, vinculo];

        handleCustoChange(updatedVinculos);

        handleSuccessAddVinculo(updatedVinculos);
    }, []);

    return (
        <QCXFormStepWizard
            initialValues={initialValues}
            onSubmit={handleSubmit}
            controlComponentProps={controlComponentProps}
            onChangeListeners={undefined}
            authInfo={authInfo}
            requiredRoles={undefined}
        >
            {({ values }) => (
                <>
                    <Grid container spacing={2} style={{ 'marginTop': '20px' }} xs={12} sm={12} md={12}>
                        <Grid item xs={12} sm={4} md={4} lg={4} >
                            <QCXFinalTextField
                                id="text-field-code"
                                key="text-field-code"
                                name="numeroMaster"
                                label={t('com.muralis.qcx.agenciamento.numero_processo')}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid container style={{ 'marginTop': '20px' }} xs={12}>
                        <QCXFinalBondManagerOld
                            isParentConsult={isConsult}
                            reducerConfig={referenciaClienteReducerConfig}
                            listProps={referenciaClienteListProps}
                            formProps={referenciaClienteFormProps}
                            disableConsult
                            controlButtonsGridProps={{
                                add: {
                                    xs: 12,
                                    sm: 4,
                                    md: 4,
                                },
                                update: {
                                    xs: 6,
                                    sm: 2,
                                    md: 2,
                                },
                                clear: {
                                    xs: 6,
                                    sm: 2,
                                    md: 2,
                                },
                            }}
                            handleAdd={handleAdicionarMasterCusto}
                            handleUpdate={handleAtualizarCustos}
                            handleAlreadyExists={handleAlreadyExists}
                        >
                            {({ loading }) => (
                                <>
                                    <Grid container spacing={2} style={{ 'marginTop': '20px' }} xs={12} sm={12} md={12}>
                                        <Grid item xs={12} sm={4} md={4}>
                                            <QCXSelectDespesaReceitaAutocomplete
                                                id="master-despesa-receita-field"
                                                key="master-despesa-receita-field"
                                                name="ignorableFields.referenciaCliente.despesaReceita.id"
                                                filterParams={configureDespesaReceitaFilterPredicateWith}
                                                label="Despesa / Receita"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={4}>
                                            <QCXSelectMoedaAutocomplete
                                                id="master-moeda-field"
                                                key="master-moeda-field"
                                                name="ignorableFields.referenciaCliente.moeda.id"
                                                label="Moeda"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2} md={2} lg={2}>
                                            <QCXFinalSelectGenericoAutocomplete
                                                id="master-tipo-pagamento-field"
                                                key="master-tipo-pagamento-field"
                                                name="ignorableFields.referenciaCliente.tipoPagamento"
                                                label="Tipo de Frete"
                                                optionsProp={[{ label: 'Prepaid', value: 'PREPAID' },
                                                    { label: 'Collect', value: 'COLLECT' }]}
                                                fieldProps={{ validate: undefined }}
                                                disabled={isConsult}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2} style={{ 'marginTop': '20px' }} xs={12} sm={12} md={12}>
                                        <Grid item xs={12} sm={4} md={4}>
                                            <QCXFinalAlternativeCurrencyField
                                                id="master-valor-comprar-field"
                                                name="ignorableFields.referenciaCliente.valorComprar"
                                                label="Valor Comprar"
                                                key="valor-valor-comprar-field"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2} md={2} lg={2}>
                                            <QCXFinalSelectGenericoAutocomplete
                                                id="master-declarado-field"
                                                key="master-declarado-field"
                                                name="ignorableFields.referenciaCliente.declarado"
                                                label="Declarado"
                                                optionsProp={[{ label: 'SIM', value: 'SIM' },
                                                    { label: 'NÃO', value: 'NAO' }]}
                                                fieldProps={{ validate: undefined }}
                                                disabled={isConsult}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}

                        </QCXFinalBondManagerOld>

                    </Grid>
                </>
            )}
        </QCXFormStepWizard>
    )

}